import {
  getRequestGateway,
  patchRequestGateway,
  postRequestGateway,
  postRequestFileGateway
} from '@/services/api.service'
import { AxiosError } from 'axios'
export type AttachmentStatus = 'ARCHIVED' | 'ACTIVE'
export type AttachmentType = 'FILE' | 'LINK' | 'WMS' | 'ACT'
export type Direction = 'asc' | 'desc'
export type MIMEType = 'DOCX' | 'DOC' | 'JPG' | 'PNG' | 'PDF' | 'XLSX' | 'XLS'

export interface Attachment {
  actCategory?: string
  actStatus?: string
  actType?: string
  attachmentId: string
  attachmentType: AttachmentType //FILE, LINK, WMS or ACT
  createdDate?: string //Date
  entityId?: string
  entityType: string
  fileSize?: number
  fileType?: string
  lastModifiedDate?: string //date
  metadata?: ExtractedMetadata
  name: string
  realmId?: string
  status: AttachmentStatus
  storageName?: string
  uploadedBy?: string
  url: string
  // below for wms links only, to be broken out in the future
  linkSource?: string
  loginRequired?: boolean
  sourceLayers?: string[]
}

export type AttachmentsPage<T> = {
  content: T[]
  pageable: {
    pageNumber: number
    pageSize: number
    sort: { empty: boolean; unsorted: boolean; sorted: boolean }
    offset: number
    unpaged: boolean
    paged: boolean
  }
  totalPages: number
  totalElements: number
  last: boolean
  size: number
  number: number
  sort: { empty: boolean; unsorted: boolean; sorted: boolean }
  first: boolean
  numberOfElements: number
  empty: boolean
}

export type CreateActAttachment = {
  entityId: string
  name: string
  entityType: string
  actCategoryId: string
  actTypeId: string
  actStatusId: string
}
export type CreateLinkAttachment = {
  entityId: string
  name: string
  url: string
  entityType: string
}
export type CreateFileAttachment = {
  entityId: string
  name: string
  entityType: string
}
interface DocumentMetadata {
  lastModified?: string
  createdDate?: string
  creator?: string
  lastAuthor?: string
  nrOfPages?: number
}
export interface ExtractedMetadata {
  contentType?: string //"application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  mimeType?: MIMEType
  imageMetadata?: ImageMetadata
  documentMetadata?: DocumentMetadata
}
interface ImageMetadata {
  lastModified?: string
  width?: string // "1200 pixels" or "1200"
  length?: string // "1200"
  height?: string // "1200 pixels or "1200"
  xresolution?: string // "72 dots", due to serialization issues on backend, accept both lower and upper case R
  xResolution?: string // "72 dots", due to serialization issues on backend, accept both lower and upper case R
  yresolution?: string // "72 dots", due to serialization issues on backend, accept both lower and upper case R
  yResolution?: string // "72 dots", due to serialization issues on backend, accept both lower and upper case R
  fileSize?: number
}
export type UpdateLinkAttachment = {
  entityType: string
  name?: string
  url?: string
  // below for wms links only, to be broken out in the future
  linkSource?: string
  loginRequired?: boolean
  sourceLayers?: string[]
}

export async function archiveAttachments(attachmentIds: string[]) {
  const endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/_archive`
  const body = { ids: attachmentIds }
  return await postRequestGateway(endpoint, body)
}

export async function deleteAttachments(attachmentIds: string[]) {
  const endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/_delete`
  const body = { ids: attachmentIds }
  return await postRequestGateway(endpoint, body)
}

export async function getSingleAttachment(attachmentId: string): Promise<Attachment | undefined> {
  const endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/${attachmentId}`
  try {
    const response = await getRequestGateway<Attachment>(endpoint)
    return response?.data
  } catch (error: any) {
    return checkErrorCode(error)
  }
}

export async function postActAttachment(
  entityId: string,
  file: File,
  entityType: string,
  actCategoryId: string,
  actTypeId: string,
  actStatusId: string
) {
  const createAttachment: CreateActAttachment = {
    entityId,
    name: file.name,
    entityType,
    actCategoryId,
    actTypeId,
    actStatusId
  }
  const json = JSON.stringify(createAttachment)
  const blob = new Blob([json], {
    type: 'application/json'
  })
  const formData = new FormData()
  formData.append('body', blob)
  formData.append('file', file)
  const endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/act`
  return await postRequestFileGateway(endpoint, formData)
}

export async function postFileAttachment(entityId: string, file: File, entityType: string) {
  const createAttachment: CreateFileAttachment = {
    entityId,
    name: file.name,
    entityType
  }
  const json = JSON.stringify(createAttachment)
  const blob = new Blob([json], {
    type: 'application/json'
  })
  const formData = new FormData()
  formData.append('body', blob)
  formData.append('file', file)
  const endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/file`
  return await postRequestFileGateway(endpoint, formData)
}

export async function postLinkAttachment(body: CreateLinkAttachment) {
  const endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/link`
  return await postRequestGateway(endpoint, body)
}

export async function getAttachmentsPage(
  entityId: string,
  attachmentTypes: AttachmentType | AttachmentType[],
  page: number,
  size: number,
  sort: string = 'name,asc', // For sorting, write as RequestParam sort=<field name>,asc or <field name>,desc
  searchFilter: string | null = null, // Not required, default null
  withChildren: boolean = false, // Not required, default false
  statusFilter: AttachmentStatus = 'ACTIVE' // Not required, default 'ACTIVE'
): Promise<AttachmentsPage<Attachment> | undefined> {
  let endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/v2?entityId=${entityId}&attachmentTypes=${attachmentTypes}&withChildren=${withChildren}&statusFilter=${statusFilter}&page=${page}&size=${size}&sort=${sort}`
  if (searchFilter?.length) endpoint = endpoint + `&searchFilter=${searchFilter}`

  try {
    const response = await getRequestGateway<AttachmentsPage<Attachment>>(endpoint)
    return response?.data
  } catch (error: any) {
    return checkErrorCode(error)
  }
}

export async function updateAttachment(attachmentId: string, body: UpdateLinkAttachment) {
  const endpoint = `${process.env.VUE_APP_API_GW_BASE_URL}/attachments/${attachmentId}`
  try {
    return await patchRequestGateway(endpoint, body, null)
  } catch (error: any) {
    return checkErrorCode(error)
  }
}

function checkErrorCode(error: AxiosError) {
  if (error.response?.status.toString().startsWith('5')) {
    throw error
  }
  return undefined
}

/*
Regarding ACT attachments:
Update and delete as usual for attachments.

A new endpoint which can be handy (to fetch all Attachments connected to a File)
GET attachments/file/{fileId} = List<AttachmentDto>
*/
